// src/pages/TestOrders/sections/PatientDemographicsSection.jsx
import {
    Box,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    VStack
} from '@chakra-ui/react';
import { useState } from 'react';
import Select from 'react-select';
import { AllUsStates } from '../../../../Utils';

const raceOptions = [
    { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native' },
    { value: 'Asian', label: 'Asian' },
    { value: 'Black or African American', label: 'Black or African American' },
    { value: 'Multi-racial', label: 'Multi-racial' },
    { value: 'Native Hawaiian or other Pacific Islander', label: 'Native Hawaiian or other Pacific Islander' },
    { value: 'White', label: 'White' },
    { value: 'Other', label: 'Other' },
    { value: 'Unknown', label: 'Unknown' },
    { value: 'Not shared', label: 'Prefers not to share' },
];

const ethnicityOptions = [
    { value: 'Hispanic or Spanish origin', label: 'Hispanic or Spanish origin' },
    { value: 'Not Hispanic or Spanish origin', label: 'Not Hispanic or Spanish origin' },
    { value: 'Not shared', label: 'Prefers not to share' },
];

const PatientDemographicsSection = ({ formData, setFormData }) => {
    const today = new Date().toISOString().split('T')[0];
    const [displaySsn, setDisplaySsn] = useState("");
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <>
            <Box p={4} borderWidth="1px" rounded="md" shadow="sm">
                <Heading size="md" mb={4}>
                    Patient Demographics
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    {/* First Name */}
                    <FormControl isRequired>
                        <FormLabel>First Name</FormLabel>
                        <Input
                            name="firstName"
                            value={formData.firstName || ''}
                            onChange={handleChange}
                        />
                        {!formData.firstName && (
                            <FormErrorMessage>First name is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Middle Initials (optional) */}
                    <FormControl>
                        <FormLabel>Middle Initials</FormLabel>
                        <Input
                            name="middleInitials"
                            value={formData.middleInitials || ''}
                            onChange={handleChange}
                        />
                    </FormControl>

                    {/* Last Name */}
                    <FormControl isRequired>
                        <FormLabel>Last Name</FormLabel>
                        <Input
                            name="lastName"
                            value={formData.lastName || ''}
                            onChange={handleChange}
                        />
                        {!formData.lastName && (
                            <FormErrorMessage>Last name is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Date of Birth */}
                    <FormControl isRequired>
                        <FormLabel>Date of Birth</FormLabel>
                        <Input
                            type="date"
                            max={today}
                            name="dob"
                            value={formData.dob || ''}
                            onChange={handleChange}
                        />
                        {formData.dob && formData.dob > today && (
                            <FormErrorMessage>DOB cannot be in the future</FormErrorMessage>
                        )}
                        {!formData.dob && (
                            <FormErrorMessage>Date of Birth is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Gender */}
                    <FormControl isRequired>
                        <FormLabel>Gender</FormLabel>
                        <Select
                            options={[
                                { value: 'M', label: 'Male' },
                                { value: 'F', label: 'Female' },
                                { value: 'X', label: 'Prefer not to say' },
                            ]}
                            value={
                                formData.gender
                                    ? [
                                        { value: 'M', label: 'Male' },
                                        { value: 'F', label: 'Female' },
                                        { value: 'X', label: 'Prefer not to say' },
                                    ].find((opt) => opt.value === formData.gender)
                                    : null
                            }
                            onChange={(opt) => setFormData({ ...formData, gender: opt?.value || '' })}
                            placeholder="Select Gender"
                        />
                        {!formData.gender && (
                            <FormErrorMessage>Gender is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Ethnicity */}
                    <FormControl isRequired>
                        <FormLabel>Ethnicity</FormLabel>
                        <Select
                            options={ethnicityOptions}
                            value={
                                formData.ethnicity
                                    ? ethnicityOptions.find((opt) => opt.value === formData.ethnicity)
                                    : null
                            }
                            onChange={(opt) => setFormData({ ...formData, ethnicity: opt?.value || '' })}
                            placeholder="Select Ethnicity"
                        />
                        {!formData.ethnicity && (
                            <FormErrorMessage>Ethnicity is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Race */}
                    <FormControl isRequired>
                        <FormLabel>Race</FormLabel>
                        <Select
                            options={raceOptions}
                            value={
                                formData.race
                                    ? raceOptions.find((opt) => opt.value === formData.race)
                                    : null
                            }
                            onChange={(opt) => setFormData({ ...formData, race: opt?.value || '' })}
                            placeholder="Select Race"
                        />
                        {!formData.race && (
                            <FormErrorMessage>Race is required</FormErrorMessage>
                        )}
                    </FormControl>

                    {/* Race */}
                    <FormControl isRequired>
                        <FormLabel>SSN</FormLabel>
                        <Input
                            name="ssn"
                            value={displaySsn}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, "");
                                setFormData({ ...formData, ssn: val });
                                setDisplaySsn(val);
                            }}
                            onBlur={() => {
                                if (formData.ssn) {
                                    const masked = formData.ssn.replace(/\d(?=\d{4})/g, "•");
                                    setDisplaySsn(masked);
                                }
                            }}
                            onFocus={() => setDisplaySsn(formData.ssn)}
                            inputMode="numeric"
                        />

                        {!formData.ssn && (
                            <FormErrorMessage>SSN is required</FormErrorMessage>
                        )}
                    </FormControl>


                </SimpleGrid>
            </Box>


            <Box borderWidth="1px" borderRadius="md" p={4}>
                <Heading size="md" mb={4}>
                    Contact Informations
                </Heading>

                <VStack spacing={4} align="stretch">
                    {/* Address 1 + 2 same line */}
                    <HStack>
                        <FormControl>
                            <FormLabel>Address 1</FormLabel>
                            <Input
                                name="address1"
                                value={formData.address1 || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Address 2</FormLabel>
                            <Input
                                name="address2"
                                value={formData.address2 || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </HStack>

                    {/* City, State, Zip */}
                    <HStack>
                        <FormControl>
                            <FormLabel>City</FormLabel>
                            <Input
                                name="city"
                                value={formData.city || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>State</FormLabel>

                            <Select
                                options={AllUsStates}
                                value={AllUsStates.find((s) => s.value === formData.state) || null}
                                onChange={(opt) => setFormData({ ...formData, state: opt ? opt.value : "" })}
                            />



                        </FormControl>
                        <FormControl>
                            <FormLabel>Zip</FormLabel>
                            <Input
                                name="zip"
                                value={formData.zip || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </HStack>

                    {/* Email + Phone with mutually exclusive type */}
                    <HStack>
                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input
                                name="email"
                                value={formData.email || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Phone</FormLabel>
                            <Input
                                name="phone"
                                value={formData.phone || ""}
                                onChange={handleChange}
                            />
                        </FormControl>
                        <HStack align="center" pt={6}>
                            <Checkbox
                                isChecked={formData.phoneType === "home"}
                                onChange={() =>
                                    setFormData({ ...formData, phoneType: "home" })
                                }
                            >
                                Home
                            </Checkbox>
                            <Checkbox
                                isChecked={formData.phoneType === "mobile"}
                                onChange={() =>
                                    setFormData({ ...formData, phoneType: "mobile" })
                                }
                            >
                                Mobile
                            </Checkbox>
                        </HStack>
                    </HStack>
                </VStack>
            </Box>

        </>
    );
};

export default PatientDemographicsSection;
